// KI-Labor (Förderprojekt): lokales Modell mit und ohne Anpassung gegen Gemini und feste Regeln.
// Zeigt die Messungen aus var/ki_labor (Training/Tests laufen offline auf dem Arbeitsrechner).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";

const API = `${API_BASE}/ki-labor/api`;
const FELDER = [
  ["exakt", "Name exakt"], ["praefix", "Präfix"], ["datum", "Datum"], ["absender", "Absender"], ["nummern", "Nummern"], ["kern", "Präfix + Datum + Absender"],
];
const STATUS_FARBE = { "läuft": "#2563eb", "Messung": "#6b7280", "Vorhaben": "#c2410c" };

const S = {
  page: { maxWidth: 1250, margin: "0 auto", padding: "24px 20px 60px", color: "var(--color-gray-800)" },
  h1: { fontSize: "1.5rem", margin: "0 0 4px" },
  sub: { color: "var(--color-gray-600)", margin: "0 0 16px", fontSize: "0.9rem" },
  card: { background: "var(--bg-card)", border: "1px solid var(--color-gray-200)", borderRadius: 10, padding: 14, marginBottom: 16 },
  tab: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--color-gray-300)", whiteSpace: "nowrap" },
  td: { padding: "6px 8px", borderBottom: "1px solid var(--color-gray-200)", verticalAlign: "top" },
  btn: { padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-gray-300)", background: "var(--bg-card)", cursor: "pointer", fontSize: "0.85rem", color: "inherit" },
  bar: { height: 8, borderRadius: 4, background: "var(--color-gray-200)", overflow: "hidden", marginTop: 3 },
};

function Balken({ wert, n, farbe }) {
  const p = n ? Math.round((100 * wert) / n) : 0;
  return (
    <div>
      <div style={{ whiteSpace: "nowrap" }}><b>{wert}</b> <span style={{ color: "var(--color-gray-500)" }}>/ {n}</span> <span style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>{p} %</span></div>
      <div style={S.bar}><div style={{ width: `${p}%`, height: "100%", background: farbe }} /></div>
    </div>
  );
}

export default function KiLaborPage() {
  const navigate = useNavigate();
  const [ueb, setUeb] = useState(null);
  const [menge, setMenge] = useState("t2");
  const [docs, setDocs] = useState(null);
  const [nurFehler, setNurFehler] = useState(true);
  const [q, setQ] = useState("");
  const [fehler, setFehler] = useState("");

  useEffect(() => {
    fetch(`${API}/uebersicht`).then((r) => r.json()).then(setUeb).catch((e) => setFehler(String(e)));
  }, []);
  useEffect(() => {
    setDocs(null);
    fetch(`${API}/dokumente?menge=${menge}`).then((r) => r.json()).then(setDocs).catch((e) => setFehler(String(e)));
  }, [menge]);

  const zeilen = ueb?.mengen?.[menge] || [];
  const liste = useMemo(() => {
    const w = q.toLowerCase().split(/\s+/).filter(Boolean);
    return (docs?.dokumente || []).filter((d) =>
      (!nurFehler || Object.values(d.vorschlaege).some((v) => !v.exakt)) &&
      (!w.length || w.every((x) => (d.soll + " " + Object.values(d.vorschlaege).map((v) => v.ist || "").join(" ")).toLowerCase().includes(x))));
  }, [docs, nurFehler, q]);

  return (
    <div style={S.page}>
      <button style={S.btn} onClick={() => navigate(-1)}>← Zurück</button>
      <h1 style={{ ...S.h1, marginTop: 12 }}>🧪 KI-Labor – lokales Modell, das aus unserer Ablage lernt</h1>
      <p style={S.sub}>
        Vergleich auf echten, von uns bestätigten Dokumenten: feste Regeln, Gemini (Cloud) und das lokale Modell Llama 3.1 8B – ohne Anpassung
        und mit den Bausteinen des Vorhabens (B: LoRA-Adapter aus unserem Archiv, A: phonetische Absender-Kandidaten, C: OCR-Unsicherheit).
        Das lokale Modell läuft vollständig im Haus, keine Daten gehen in eine Cloud.
      </p>
      {fehler && <div style={{ color: "var(--color-error)" }}>{fehler}</div>}

      <div style={S.card}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <b>Testmenge:</b>
          {["t2", "t1"].map((m) => (
            <button key={m} style={{ ...S.btn, ...(menge === m ? { background: "#2563eb", borderColor: "#2563eb", color: "#fff", fontWeight: 600 } : {}) }} onClick={() => setMenge(m)}>
              {m === "t2" ? "Betrieb 07.09.–09.10.2026" : "Kontrollierter Vergleich (60)"}
            </button>
          ))}
          <span style={{ fontSize: "0.8rem", color: "var(--color-gray-500)" }}>{ueb?.beschreibung?.[menge]}</span>
        </div>
        <table style={S.tab}>
          <thead><tr><th style={S.th}>Verfahren</th>{FELDER.map(([k, l]) => <th key={k} style={S.th}>{l}</th>)}<th style={S.th}>Ø Sek.</th></tr></thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.lauf}>
                <td style={S.td}>
                  <span style={{ fontSize: "0.7rem", fontWeight: 700, color: STATUS_FARBE[z.status], border: `1px solid ${STATUS_FARBE[z.status]}`, borderRadius: 8, padding: "0 6px", marginRight: 6 }}>{z.status}</span>
                  {z.label}
                </td>
                {FELDER.map(([k]) => <td key={k} style={{ ...S.td, minWidth: 110 }}><Balken wert={z[k]} n={z.n} farbe={STATUS_FARBE[z.status]} /></td>)}
                <td style={S.td}>{z.sek ?? "–"}</td>
              </tr>
            ))}
            {!zeilen.length && <tr><td style={S.td} colSpan={8}>Noch keine Ergebnisse.</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={S.card}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
          <b>Einzelne Dokumente ({liste.length})</b>
          <label style={{ fontSize: "0.85rem" }}><input type="checkbox" checked={nurFehler} onChange={(e) => setNurFehler(e.target.checked)} /> nur mit Abweichung</label>
          <input style={{ ...S.btn, flex: "1 1 240px", cursor: "text" }} placeholder="Suchen …" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={S.tab}>
            <thead><tr><th style={S.th}>Bestätigter Name</th>{(docs?.laeufe || []).map((l) => <th key={l.lauf} style={{ ...S.th, whiteSpace: "normal", minWidth: 160 }}>{l.label}</th>)}</tr></thead>
            <tbody>
              {liste.slice(0, 300).map((d) => (
                <tr key={d.soll}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{d.soll}</td>
                  {(docs?.laeufe || []).map((l) => {
                    const v = d.vorschlaege[l.lauf];
                    return <td key={l.lauf} style={{ ...S.td, background: !v ? "transparent" : v.exakt ? "rgba(22,163,74,.12)" : v.kern ? "rgba(234,179,8,.12)" : "rgba(220,38,38,.08)" }}>{v?.ist || "–"}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)", marginTop: 6 }}>Grün = exakt, gelb = Präfix, Datum und Absender richtig, rot = abweichend.</div>
      </div>
    </div>
  );
}
